import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { ZagButton } from '../button/button';
import { ZagIcon } from '../icon/icon';

/**
 * Compact card that summarises the current selection state.
 * Reusable alternative to the sticky SelectionToolbarComponent —
 * can be placed anywhere in the layout (sidebar, floating panel, etc.).
 */
@Component({
  selector: 'zag-selection-summary-card',
  imports: [TranslatePipe, ZagButton, ZagIcon],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="zag-selection-summary-card" role="region" [attr.aria-label]="'common.selectionMode' | translate">
      <div class="zag-selection-summary-card__header">
        <span zagIcon="checklist" class="zag-selection-summary-card__icon" aria-hidden="true"></span>
        <span class="zag-selection-summary-card__title">{{ 'common.selectionMode' | translate }}</span>
      </div>

      <div class="zag-selection-summary-card__count">
        <span class="zag-selection-summary-card__count-value">{{ selectedCount() }}</span>
        <span class="zag-selection-summary-card__count-divider">/</span>
        <span class="zag-selection-summary-card__count-total">{{ totalCount() }}</span>
      </div>

      @if (allMatchingActive()) {
        <div class="zag-selection-summary-card__all-matching" role="status">
          <span zagIcon="select_all" class="zag-selection-summary-card__all-matching-icon" aria-hidden="true"></span>
          <span>{{ 'common.selectAllMatching' | translate }}</span>
        </div>
      }

      <div class="zag-selection-summary-card__actions">
        <button zagButton type="button" variant="primary" [disabled]="selectedCount() === 0" (click)="bulkEdit.emit()">
          {{ 'common.bulkEdit' | translate }}
        </button>
        <button zagButton type="button" variant="ghost" (click)="cancelSelection.emit()">
          {{ 'common.cancelSelection' | translate }}
        </button>
      </div>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
      }

      .zag-selection-summary-card {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-md);
        padding: var(--spacing-lg);
        background-color: var(--surface-color-primary);
        border: 1px solid var(--border-color-default);
        border-radius: var(--border-radius-lg);
        box-shadow: var(--shadow-md);
        min-width: 200px;
      }

      .zag-selection-summary-card__header {
        display: flex;
        align-items: center;
        gap: var(--spacing-sm);
        font-size: var(--font-size-sm);
        font-weight: var(--font-weight-medium);
        color: var(--text-color-secondary);
        text-transform: uppercase;
        letter-spacing: 0.05em;
      }

      .zag-selection-summary-card__icon {
        font-size: var(--icon-size-compact);
        color: var(--color-primary);
      }

      .zag-selection-summary-card__count {
        display: flex;
        align-items: baseline;
        gap: var(--spacing-xs);
      }

      .zag-selection-summary-card__count-value {
        font-size: var(--font-size-xl);
        font-weight: var(--font-weight-bold);
        color: var(--color-primary);
        line-height: 1;
      }

      .zag-selection-summary-card__count-divider {
        font-size: var(--font-size-xl);
        color: var(--text-color-tertiary);
      }

      .zag-selection-summary-card__count-total {
        font-size: var(--font-size-xl);
        color: var(--text-color-secondary);
      }

      .zag-selection-summary-card__all-matching {
        display: flex;
        align-items: center;
        gap: var(--spacing-xs);
        font-size: var(--font-size-sm);
        color: var(--color-primary);
        font-weight: var(--font-weight-medium);
      }

      .zag-selection-summary-card__all-matching-icon {
        font-size: var(--icon-size-compact);
      }

      .zag-selection-summary-card__actions {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-sm);
      }
    `,
  ],
})
export class SelectionSummaryCardComponent {
  readonly selectedCount = input.required<number>();
  readonly totalCount = input.required<number>();
  readonly allMatchingActive = input<boolean>(false);

  readonly bulkEdit = output<void>();
  readonly cancelSelection = output<void>();
}
